import {
  Select,
  type SelectItem,
  type SelectProps,
} from '@vrplatform/components';
import { IconButton } from '@vrplatform/components-legacy';
import { useTeam } from '@vrplatform/data';
import { ChevronIcon } from '@vrplatform/icons';
import {
  Group,
  Stack,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { forwardRef, type JSX } from 'react';

export type TeamSwitchTriggerProps = {
  opened: boolean;
  label?: string;
  isNavigationExpanded: boolean;
  canSwitch: boolean;
  tone?: 'navbar' | 'default';
};

type TeamSwitchSelectProps = {
  canSwitch: boolean;
  containerPaddingTop?: number | string;
  customActionTop?: SelectProps<string>['customActionTop'];
  customBottomActions?: SelectProps<string>['customBottomActions'];
  /** Keeps the dropdown closed. */
  disabled?: boolean;
  dropdownProps?: SelectProps<string>['dropdownProps'];
  header?: string | null;
  isNavigationExpanded?: boolean;
  loadingQuery: boolean;
  onOpenChange?: (opened: boolean) => void;
  pinnedItems?: SelectProps<string>['pinnedItems'];
  queryData: Omit<
    NonNullable<SelectProps<string>['infiniteData']>,
    'setSearch'
  >;
  renderTrigger?: (props: TeamSwitchTriggerProps) => JSX.Element;
  setSearch: (search: string) => void;
  setValue: (value: SelectItem | null) => void;
  tone?: 'navbar' | 'default';
  value: SelectItem | null;
};

export const TeamSwitchSelect = ({
  canSwitch,
  containerPaddingTop = 5,
  customActionTop,
  customBottomActions,
  disabled,
  dropdownProps,
  header,
  isNavigationExpanded = true,
  loadingQuery,
  onOpenChange,
  pinnedItems,
  queryData,
  renderTrigger,
  setSearch,
  setValue,
  tone = 'navbar',
  value,
}: TeamSwitchSelectProps) => {
  const { colorScheme } = useMantineColorScheme();
  const { colors } = useMantineTheme();
  const isLightSurface = tone === 'default';
  const foregroundColor = !isLightSurface
    ? '#fff'
    : colorScheme === 'dark'
      ? colors.neutral[0]
      : colors.neutral[9];

  return (
    <Stack
      gap={0}
      pt={containerPaddingTop}
      sx={{
        color: foregroundColor,
        width: '100%',
      }}
    >
      {!!header && isNavigationExpanded && (
        <Text sx={{ opacity: 0.8 }} size="xs">
          {header}
        </Text>
      )}
      <Select
        type="single"
        setValue={setValue}
        infiniteData={{ ...queryData, setSearch }}
        dropdownProps={{
          withinPortal: true,
          position: isNavigationExpanded ? 'bottom' : 'bottom-start',
          width: 260,
          ...dropdownProps,
        }}
        customActionTop={customActionTop}
        customBottomActions={customBottomActions}
        pinnedItems={pinnedItems}
        onOpenChange={onOpenChange}
        inputProps={{
          disabled,
          loadingQuery,
        }}
        value={value}
      >
        {({ opened, value }) => {
          const triggerProps = {
            opened,
            canSwitch,
            isNavigationExpanded,
            label: Array.isArray(value) ? undefined : value?.label,
            tone,
          } satisfies TeamSwitchTriggerProps;

          return (
            renderTrigger?.(triggerProps) || (
              <DefaultTeamSwitchTrigger {...triggerProps} />
            )
          );
        }}
      </Select>
    </Stack>
  );
};

const DefaultTeamSwitchTrigger = forwardRef<
  HTMLDivElement,
  TeamSwitchTriggerProps
>(({ isNavigationExpanded, canSwitch, label, tone = 'navbar' }, ref) => {
  const [{ isGL }] = useTeam();
  const { colorScheme } = useMantineColorScheme();
  const { colors } = useMantineTheme();
  const isLightSurface = tone === 'default';
  const textColor = !isLightSurface
    ? undefined
    : colorScheme === 'dark'
      ? colors.neutral[0]
      : colors.neutral[9];
  const hoverBackground = !isLightSurface
    ? '#5C617840'
    : colorScheme === 'dark'
      ? colors.dark[4]
      : colors.neutral[1];
  const chevronColor = !isLightSurface
    ? '#ffffff80'
    : colorScheme === 'dark'
      ? colors.neutral[4]
      : colors.neutral[5];

  return (
    <Group
      wrap="nowrap"
      gap={0}
      ref={ref}
      pl={isNavigationExpanded ? 8 : 0}
      ml={isNavigationExpanded ? -8 : 0}
      sx={{
        borderRadius: 7,
        maxWidth: isGL ? 140 : 160,
        width: '100%',
        justifyContent: isNavigationExpanded ? 'space-between' : 'center',
        display: !isNavigationExpanded && !canSwitch ? 'none' : undefined,
        cursor: canSwitch ? 'pointer' : 'default',
        ':hover': {
          backgroundColor: canSwitch ? hoverBackground : undefined,
        },
        '&, *': {
          overflowY: 'hidden',
        },
      }}
    >
      {isNavigationExpanded && (
        <Group gap={5} wrap="nowrap" pt={4} sx={{ height: '100%' }}>
          <Text
            component="span"
            sx={{
              maxWidth: '100%',
              display: 'block',
              height: 20,
              textOverflow: 'ellipsis',
              overflowX: 'hidden',
              whiteSpace: 'nowrap',
              lineHeight: 'normal',
              color: textColor,
            }}
          >
            {label}
          </Text>
        </Group>
      )}

      {canSwitch && (
        <IconButton
          component="span"
          sx={{
            ':hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <ChevronIcon size={17} color={chevronColor} />
        </IconButton>
      )}
    </Group>
  );
});
